import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ArrowLeft, Package, ShoppingBag, TrendingUp } from 'lucide-react';
import productService from '../services/productService';
import ProductGrid from './ProductGrid';

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%);
  color: white;
  padding: 2rem 1rem;
  
  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const Container = styled.div`
  max-width: 140rem;
  margin: 0 auto;
`;

const HeroSection = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 20px;
  padding: 3rem;
  margin: 9rem 0 3rem 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #3b82f6, #8b5cf6, #ec4899);
  }
  
  @media (max-width: 1024px) {
    margin: 7rem 0 2.5rem 0;
    padding: 2.5rem;
  }
  
  @media (max-width: 768px) {
    margin: 6rem 0 2rem 0;
    padding: 2rem;
    border-radius: 16px;
  }
  
  @media (max-width: 640px) {
    margin: 5rem 0 1.5rem 0;
    padding: 1.5rem;
    border-radius: 12px;
  }
  
  @media (max-width: 480px) {
    margin: 4rem 0 1rem 0;
    padding: 1rem;
    border-radius: 10px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 2rem;
  
  .header-left {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }
  
  .back-button {
    background: rgba(255, 255, 255, 0.1);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    color: white;
    padding: 0.75rem 1.25rem;
    border-radius: 12px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 500;
    transition: all 0.3s ease;
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(59, 130, 246, 0.2);
    }
  }
  
  .category-info {
    flex: 1;
  }
  
  h1 {
    font-size: clamp(2rem, 5vw, 3.5rem);
    font-weight: 800;
    margin: 0 0 0.5rem 0;
    background: linear-gradient(135deg, #3b82f6, #8b5cf6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: capitalize;
  }
  
  .subtitle {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1.1rem;
    font-weight: 500;
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .count {
      background: rgba(59, 130, 246, 0.2);
      padding: 0.25rem 0.75rem;
      border-radius: 20px;
      font-weight: 600;
      color: #60a5fa;
    }
  }
  
  .stats {
    display: flex;
    gap: 2rem;
    align-items: center;
    
    .stat {
      text-align: center;
      
      .number {
        font-size: 2rem;
        font-weight: 800;
        color: #3b82f6;
        display: block;
      }
      
      .label {
        font-size: 0.9rem;
        color: rgba(255, 255, 255, 0.7);
      }
    }
  }
  
  @media (max-width: 1024px) {
    gap: 1.5rem;
    
    .stats {
      gap: 1.5rem;
      
      .stat .number {
        font-size: 1.75rem;
      }
    }
  }
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-direction: column;
    align-items: flex-start;
    
    .header-left {
      width: 100%;
      justify-content: space-between;
    }
    
    .stats {
      gap: 1rem;
      width: 100%;
      justify-content: space-around;
      
      .stat .number {
        font-size: 1.5rem;
      }
      
      .stat .label {
        font-size: 0.8rem;
      }
    }
    
    h1 {
      font-size: clamp(1.5rem, 4vw, 2.5rem);
    }
    
    .subtitle {
      font-size: 1rem;
    }
  }
  
  @media (max-width: 640px) {
    .header-left {
      flex-direction: column;
      gap: 1rem;
      align-items: flex-start;
    }
    
    .back-button {
      padding: 0.5rem 1rem;
      font-size: 0.9rem;
    }
    
    .stats {
      gap: 0.75rem;
      
      .stat .number {
        font-size: 1.25rem;
      }
      
      .stat .label {
        font-size: 0.75rem;
      }
    }
  }
  
  @media (max-width: 480px) {
    .stats {
      gap: 0.5rem;
      
      .stat .number {
        font-size: 1.1rem;
      }
      
      .stat .label {
        font-size: 0.7rem;
      }
    }
    
    .subtitle {
      font-size: 0.9rem;
      flex-direction: column;
      align-items: flex-start;
      gap: 0.5rem;
    }
  }
`;

const ProductsSection = styled.div`
  margin-top: 3rem;
  
  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
    
    h2 {
      font-size: 1.8rem;
      font-weight: 700;
      margin: 0;
      color: white;
    }
    
    .view-options {
      display: flex;
      gap: 0.5rem;
      
      button {
        background: rgba(255, 255, 255, 0.05);
        border: 1px solid rgba(255, 255, 255, 0.1);
        color: white;
        padding: 0.5rem 1rem;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        
        &:hover, &.active {
          background: rgba(59, 130, 246, 0.2);
          border-color: #3b82f6;
        }
      }
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 6rem 2rem;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  
  .icon {
    font-size: 4rem;
    margin-bottom: 2rem;
    opacity: 0.5;
  }
  
  h2 {
    font-size: 2rem;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    color: rgba(255, 255, 255, 0.7);
    font-size: 1.1rem;
    margin-bottom: 2rem;
    max-width: 500px;
    margin-left: auto;
    margin-right: auto;
  }
  
  .back-button {
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    border: none;
    color: white;
    padding: 1rem 2rem;
    border-radius: 12px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    font-weight: 600;
    transition: all 0.3s ease;
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(59, 130, 246, 0.3);
    }
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
  
  .icon {
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const CategoryPage = () => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Charger les produits de la catégorie
  useEffect(() => {
    const loadCategoryProducts = async () => {
      try {
        setLoading(true);
        console.log('Chargement des produits pour la catégorie:', categoryName);
        
        // Récupérer tous les produits
        const allProducts = await productService.getProducts();
        
        // Filtrer par catégorie (case-insensitive)
        const categoryProducts = allProducts.filter(product => 
          product.category_name && 
          product.category_name.toLowerCase() === categoryName.toLowerCase()
        );
        
        console.log('Produits trouvés pour la catégorie', categoryName, ':', categoryProducts.length);
        setProducts(categoryProducts);
        setError(null);
      } catch (err) {
        console.error('Error loading category products:', err);
        setError('Erreur lors du chargement des produits');
      } finally {
        setLoading(false);
      }
    };

    if (categoryName) {
      loadCategoryProducts();
    }
  }, [categoryName]);

  const handleBack = () => {
    navigate(-1);
  };

  if (loading) {
    return (
      <PageWrapper>
        <Container>
          <LoadingSpinner>
            <Package size={48} className="icon" />
            <h2 style={{ marginTop: '1rem' }}>Chargement des produits...</h2>
          </LoadingSpinner>
        </Container>
      </PageWrapper>
    );
  }

  if (error) {
    return (
      <PageWrapper>
        <Container>
          <EmptyState>
            <Package className="icon" />
            <h2>Erreur de chargement</h2>
            <p>{error}</p>
            <button onClick={handleBack} className="back-button">
              <ArrowLeft size={16} />
              Retour
            </button>
          </EmptyState>
        </Container>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Container>
        <HeroSection>
          <Header>
            <div className="header-left">
              <button onClick={handleBack} className="back-button">
                <ArrowLeft size={16} />
                Retour
              </button>
              <div className="category-info">
                <h1>{categoryName}</h1>
                <div className="subtitle">
                  Catégorie de produits
                  <span className="count">{products.length} produit{products.length > 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
            <div className="stats">
              <div className="stat">
                <span className="number">{products.length}</span>
                <span className="label">Produits</span>
              </div>
              <div className="stat">
                <span className="number">{products.filter(p => p.stock > 0).length}</span>
                <span className="label">En stock</span>
              </div>
              <div className="stat">
                <span className="number">{Math.round(products.filter(p => p.stock > 0).length / products.length * 100) || 0}%</span>
                <span className="label">Disponibilité</span>
              </div>
            </div>
          </Header>
        </HeroSection>
        
        <ProductsSection>
          <div className="section-header">
            <h2>
              <ShoppingBag size={24} style={{ marginRight: '0.5rem', verticalAlign: 'middle' }} />
              Produits disponibles
            </h2>
            <div className="view-options">
              <button className="active">Grille</button>
              <button>Liste</button>
            </div>
          </div>
          
          {products.length === 0 ? (
            <EmptyState>
              <Package className="icon" />
              <h2>Aucun produit trouvé</h2>
              <p>
                Il n'y a aucun produit dans la catégorie "{categoryName}" pour le moment. 
                Essayez de chercher dans une autre catégorie ou revenez plus tard.
              </p>
              <button onClick={handleBack} className="back-button">
                <ArrowLeft size={16} />
                Retour aux produits
              </button>
            </EmptyState>
          ) : (
            <ProductGrid products={products} />
          )}
        </ProductsSection>
      </Container>
    </PageWrapper>
  );
};

export default CategoryPage;
